/**
 * Что значит каждый код шага «голос клиента» (#843, #844): предметы вопросов
 * клиента (`question_subject_v1`) и причины отказа (`decline_reason_codes_v1`).
 *
 * Сами наборы и их версии — в `@aica/api-types` (`schemas/voice-extraction.ts`):
 * их значения нужны схеме хранимого результата. Отсюда к api-types — только
 * `import type` (кольцо core ↔ api-types ломается от любого value-импорта,
 * #1076). Полноту держит тип: определения — `Record` по коду, поэтому код,
 * добавленный в набор без определения, и определение без кода не компилируются.
 *
 * Определения решают, куда модель положит ответ, поэтому их правка поднимает
 * версию набора — растяжка в `voice-extraction-codes.test.ts`, тот же порядок,
 * что у `objection_codes_v2` (#842). Границы проведены положительными
 * примерами: правило-запрет обобщается в молчание, а молчание здесь — `other`.
 */
import type { DeclineReasonCode, QuestionSubjectCode } from '@aica/api-types/schemas';
import { OBJECTION_CODE_DEFINITIONS } from './objection-code-definitions';

export const QUESTION_SUBJECT_DEFINITIONS: Readonly<Record<QuestionSubjectCode, string>> =
  {
    price_and_inclusions:
      'how much it costs and what the price includes — "how much is it?", "is delivery included?", "is there a discount?"',
    payment_and_financing:
      'how to pay — instalments, leasing, loan, deferral, prepayment, payment method, VAT invoice payment',
    delivery_and_timing:
      'when and how it arrives — delivery time, shipping cost, pickup, lead time, "can you deliver by Friday?"',
    availability:
      'whether it is in stock or can be ordered at all — "do you have it?", "when will it be back?"',
    specs_and_configuration:
      'what exactly the product is — specifications, size, compatibility, configuration, options, which model fits',
    warranty_and_service:
      'warranty terms, repair, maintenance, spare parts, service visits, returns policy',
    documents: 'contracts, invoices, certificates, tax documents and other paperwork',
    comparison:
      'how it compares — with another model, brand, offer or a competitor, "what is the difference between…?"',
    purchase_process:
      'how buying works — how to order, reserve, next steps, who to contact, how long the deal takes',
    other:
      'a real question about the product, terms or process that fits none of the above',
  };

/**
 * Коды причин отказа, которых нет у возражений. Остальные коды набора — коды
 * возражений `objection_codes_v2` (тест сверяет подмножество).
 */
export const DECLINE_ONLY_CODES = [
  'chose_competitor',
  'no_current_need',
  'not_decision_maker',
] as const satisfies readonly DeclineReasonCode[];

const DECLINE_ONLY: ReadonlySet<string> = new Set(DECLINE_ONLY_CODES);

/**
 * Which label family names a decline reason — ONE rule for the screen and the
 * monthly email: a code only declines have (`DECLINE_ONLY_CODES`) has its own
 * decline label; a code shared with the objections takes the objection's —
 * one name for «price» in both rows (#847). Each surface keeps its own key
 * prefix for the family.
 */
export function declineReasonLabelFamily(code: string): 'decline' | 'objection' {
  return DECLINE_ONLY.has(code) ? 'decline' : 'objection';
}

const objection = OBJECTION_CODE_DEFINITIONS;

/**
 * Общие с возражениями коды берут ТО ЖЕ определение, а не свою копию: «цена»
 * отказа и «цена» возражения — один ряд (#847), и разойтись они не должны.
 * Следствие: правка определения возражения поднимает и эту версию — тест
 * отпечатка ловит и это.
 */
export const DECLINE_REASON_DEFINITIONS: Readonly<Record<DeclineReasonCode, string>> = {
  price: objection.price,
  budget: objection.budget,
  financing: objection.financing,
  payment_terms: objection.payment_terms,
  timing: objection.timing,
  delivery_availability: objection.delivery_availability,
  product_fit: objection.product_fit,
  quality_reliability: objection.quality_reliability,
  trust_service: objection.trust_service,
  documents_contract: objection.documents_contract,
  unwanted_contact: objection.unwanted_contact,
  chose_competitor:
    'has already chosen or bought from another supplier — "we ordered elsewhere", "we went with X"',
  no_current_need:
    'does not need it (any more) — already bought, the problem is solved, not relevant to them. Needing it, but later, is timing',
  not_decision_maker:
    'the person on the line cannot make the purchase — someone else decides and the conversation ends there',
  other: objection.other,
};
